import React, { useCallback, useContext, useEffect, useState } from 'react';
import MTable from '../../../../Components/MTable/MTable';
import { useIntl } from 'react-intl';
import { useAptorApi } from '../../../../Api';
import { MTableToolbar, Query } from '@material-table/core';
import { IGridQueryRequest, OrderDirection } from '../../../../Api/AptorApi';
import { ChangeFilter } from './Filters';
import { useNavigate, useLocation } from 'react-router-dom';
import { useIsMounted } from '../../../../Hooks/useIsMounted';
import moment from 'moment';
import { useFilter } from '../../../../Components/ListFilter';
import { Box, Button, Checkbox, Link as MuiLink, Tooltip } from '@material-ui/core'
import { ArrowUpward, SaveAlt } from '@material-ui/icons'
import { useStyles } from "../../LawPortal.styles";
import { ModuleContext } from "../../../../Context/ModuleContext/ModuleContext";
import { ChangeAcknowledgeDialog, ChangeAcknowledgeDialogTarget } from '../../Components/ChangeAcknowledgeDialog';
import { UserContext } from '../../../../Context/UserContext/UserContext';

type UnacknowledgedChange = {
  id: number;
  number: string;
  effectiveFrom: Date;
  reqId: number;
  name: string;
  userId: number;
  userName: string;
  type: 'Law' | 'Requirement';
};

export const ChangesToAcknowledge = () => {
  const { formatMessage } = useIntl();
  const { api } = useAptorApi();
  const [filters, setFilter] = useFilter();
  const tableRef = React.useRef<any>(null);
  const isMounted = useIsMounted();
  const classes = useStyles();
  const { setHeader } = useContext(ModuleContext);
  const user = useContext(UserContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [isFiltersInitialized, setIsFiltersInitialized] = useState(false);
  const [activeDialog, setActiveDialog] = useState<UnacknowledgedChange | 'bulk'>();
  const [selectedChangeAcknowledge, setSelectedChangeAcknowledge] = useState<UnacknowledgedChange[]>([]);
  const selectedRowsCheckboxRef = React.useRef<UnacknowledgedChange[]>([]);

  useEffect(() => {
    if (isMounted && isFiltersInitialized) {
      tableRef.current && (tableRef.current as any).onQueryChange();
    }
  }, [filters, tableRef, isMounted, isFiltersInitialized]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const queryParamsMap = {
      companyUnit: 'companyUnit',
      complianceUsers: 'complianceUsers',
      category: 'category',
      area: 'area',
    };
    Object.entries(queryParamsMap).forEach(([queryKey, stateKey]) => {
      const queryValues = searchParams.getAll(queryKey);
      if (queryValues.length) {
        setFilter(stateKey, queryValues.join(',').split(',').map(num => num.toString()));
      }
    });
    setIsFiltersInitialized(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // setHeader(formatMessage({ id: 'law-portal.requirements.header', defaultMessage: 'Other requirements' }), [
    //   {
    //     title: formatMessage({ id: 'law-portal.requirements.header', defaultMessage: 'Other requirements' }),
    //     to: `/law-portal/other-requirements`,
    //   },
    //   {
    //     title: formatMessage({ id: 'law-portal.statistics.sub-menu.changes.unacknowledged' }),
    //     to: `/law-portal/other-requirements/unacknowledged`,
    //   },
    //   {
    //     title: formatMessage({ id: 'law-portal.statistics.sub-menu.changes.acknowledged' }),
    //     to: `/law-portal/other-requirements/acknowledged`,
    //   },
    // ]);
     setHeader(formatMessage({ id: 'law-portal.requirements.header', defaultMessage: 'Other requirements' }));
  }, [setHeader, formatMessage]);

  const handleDataFetch = useCallback(
    async (query: Query<UnacknowledgedChange>) => {
      if (!isFiltersInitialized) {
        return { data: [], page: 0, totalCount: 0 };
      }
      return await api.searchrequirementsUnacknowledgedChanges<UnacknowledgedChange>({
        orderDirection: query.orderDirection === 'desc' ? OrderDirection.Descending : OrderDirection.Ascending,
        orderBy: query.orderBy !== undefined ? query.orderBy.field : 'effectiveFrom',
        page: query.page,
        pageSize: query.pageSize,
        search: query.search,
        filters: filters,
      } as IGridQueryRequest);
    },
    [api, filters, isFiltersInitialized],
  );

  const renderDate = (date?: Date) => {
    return date ? moment(date).format('ll') : undefined;
  };

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

useEffect(() => {
  const updatedQueryParams = Object.keys(filters)
    .filter(key => {
      const value = filters[key];
      if (Array.isArray(value)) {
        return value.join() !== "";
      } else if (typeof value === 'object' && value !== null) {
        return value.min !== "" && value.max !== "";
      }
      return false;
    })
    .map(key => {
      const value = filters[key];
      if (Array.isArray(value)) {
        return `${key}=${encodeURIComponent(value.join())}`;
      } else if (typeof value === 'object' && value !== null) {
        return `${key}[min]=${encodeURIComponent(value.min)}&${key}[max]=${encodeURIComponent(value.max)}`;
      }
      return ''; // Fallback return to satisfy ESLint
    })
    .filter(param => param !== '') // Remove any empty strings
    .join('&');

  navigate({
    pathname: location.pathname,
    search: `?${updatedQueryParams}`,
  });
}, [filters, navigate, location.pathname]);

  const handleExport = async () => {
    const response = await api.exportrequirementsUnacknowledgedChanges();
    const blob = await response?.blob();
    const contentDisposition = response.headers.get('Content-Disposition');
    let fileName = 'RequirementExport.xlsx';
    if (contentDisposition && contentDisposition.includes('attachment')) {
      const matches = /filename=([^;]*)/.exec(contentDisposition);
      if (matches && matches[1]) {
        fileName = matches[1];
      }
    }
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const handleOpenChangeDialog = (row: UnacknowledgedChange) => {
    setActiveDialog(row);
  };

  const handleOpenBulkDialog = () => {
    setActiveDialog('bulk');
  };

  const toggleRowSelected = (row: UnacknowledgedChange, checked: boolean) => {
    const next = checked
      ? (selectedRowsCheckboxRef.current.includes(row) ? selectedRowsCheckboxRef.current : [...selectedRowsCheckboxRef.current, row])
      : selectedRowsCheckboxRef.current.filter((r) => r !== row);
    selectedRowsCheckboxRef.current = next;
    setSelectedChangeAcknowledge(next);
  };

  const handleBulkAcknowledge = async (targets: ChangeAcknowledgeDialogTarget[], comment: string, affect?: boolean) => {
    for (const target of targets) {
      if (target.reqId) {
        await api.acknowledgeLawPortalRequirementChange(target.reqId, target.id, comment, affect);
      }
    }
  };

  const refreshTable = () => {
    tableRef.current && (tableRef.current as any).onQueryChange();
    selectedRowsCheckboxRef.current = [];
    setSelectedChangeAcknowledge([]);
  };

  return (
    <>
     <Box style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px', marginBottom: '16px' }}>
        {selectedChangeAcknowledge.length > 0 && (
        <Button
          variant="outlined"
          disabled={selectedChangeAcknowledge.length === 0}
          onClick={handleOpenBulkDialog}
          style={{ fontWeight: 700, borderColor: '#c4122f', color: '#c4122f' }}
        >
          {formatMessage({ id: 'law-portal.our-law.ack.several.button' })}
        </Button>
        )}
      </Box>
      <ChangeFilter filters={filters} setFilter={setFilter} />
      <hr style={{ marginTop: '25px', marginBottom: '25px' }} />
      <MTable
        tableRef={tableRef}
        columns={[
          {
            title: formatMessage({ id: 'law-portal.requirements.header', defaultMessage: 'Other requirements' }),
            field: 'name',
          },
          {
            title: formatMessage({ id: 'law-portal.statistics.table.number' }),
            field: 'number',
            render: (row) =>  (
              <MuiLink
                component="button"
                onClick={() => handleOpenChangeDialog(row)}
                style={{ color: '#551a8b', font: 'inherit', textAlign: 'left', textDecoration: 'underline' }}
              >
                {row.number}
              </MuiLink>
            )
          },
          {
            title: formatMessage({ id: 'law-portal.statistics.table.effectiveFrom' }),
            field: 'effectiveFrom',
            render: (row) => renderDate(row.effectiveFrom),
          },
          {
            title: formatMessage({ id: 'law-portal.statistics.table.user' }),
            field: 'userName',
          },
          {
            title: '',
            sorting: false,
            render: (row) => (
             (Number(row.userId) == Number(user.id)&& <Checkbox
                checked={selectedRowsCheckboxRef.current.includes(row)}
                disabled={Number(row.userId) !== Number(user.id)}
                onChange={(_, checked) => toggleRowSelected(row, checked)}
              />)
            ),
             width: '5%',
          },
        ]}
        data={handleDataFetch}
        components={{
          Toolbar: (props) => (
            <div className={classes.toolbar}>
              <MTableToolbar {...props} />
              <SaveAlt style={{ cursor: 'pointer' }} onClick={() => handleExport()} />
            </div>
          ),
        }}
      />
      {showScrollTop && (
        <Tooltip title={formatMessage({ id: "bottom.to.top.btn.tooltip.text", defaultMessage: "Back to top" })}>
          <Button
            onClick={() => {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            className={classes.topButton}
          >
            <ArrowUpward style={{ fontSize: 20 }} />
          </Button>
        </Tooltip>
      )}
      <ChangeAcknowledgeDialog
        target={activeDialog === 'bulk' ? selectedChangeAcknowledge : activeDialog}
        onClose={() => {
          setActiveDialog(undefined);
          if (activeDialog === 'bulk') {
            selectedRowsCheckboxRef.current = [];
            setSelectedChangeAcknowledge([]);
          }
        }}
        onAcknowledged={refreshTable}
        onAcknowledge={(t, comment, affect) => handleBulkAcknowledge([t], comment, affect)}
        onBulkAcknowledge={handleBulkAcknowledge}
      />
    </>
  );
};
